import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import supabase from '../supabase/supabase';

const { FiCheck } = FiIcons;

const Contact = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    presence: ''
  });
  const [status, setStatus] = useState('idle'); // idle, submitting, success, error

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');

    try {
      const { error } = await supabase.from('leads').insert({
        full_name: formData.fullName,
        email: formData.email,
        social_handle: formData.presence,
        plan_selected: 'General Inquiry', // Fixed value as requested
        source: 'calibration',            // Fixed value as requested
        intent_level: 'medium'            // Fixed value as requested
      });

      if (error) throw error;
      setStatus('success');
      setFormData({ fullName: '', email: '', presence: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      setStatus('error');
    }
  };

  return (
    <section id="start-calibration" className="bg-[#BEF264] py-16 md:py-32 section-curve-up">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-12 md:gap-24 items-start">
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }} >
            <div className="flex items-center gap-4 mb-4">
              <div className="w-8 h-[2px] bg-black" />
              <span className="text-black font-black uppercase tracking-[0.4em] text-[10px]"> Direct Access </span>
            </div>
            <h2 className="text-4xl md:text-8xl font-black text-black tracking-tighter leading-none mb-8 md:mb-12 uppercase italic"> START <br /> THE <span className="not-italic">CALIBRATION.</span> </h2>
            <p className="text-black/80 font-medium text-base md:text-xl max-w-md leading-relaxed tracking-tight mb-0 md:mb-12"> We operate with a limited roster to maintain our standards. Inquire now to secure your Q1 slot. </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }} viewport={{ once: true }} className="bg-black p-7 md:p-12 rounded-[2.5rem] md:rounded-[3rem] relative overflow-hidden" >
            {status === 'success' ? (
              <motion.div 
                initial={{ opacity: 0 }} 
                animate={{ opacity: 1 }} 
                className="flex flex-col items-center justify-center text-center py-12"
              >
                <div className="w-16 h-16 rounded-full bg-[#BEF264]/10 flex items-center justify-center mb-6">
                  <SafeIcon icon={FiCheck} className="w-8 h-8 text-[#BEF264]" />
                </div>
                <h3 className="text-white font-black uppercase tracking-widest text-xl mb-4">Inquiry Received</h3>
                <p className="text-white/60 text-sm font-medium tracking-wide max-w-xs mb-8">
                  We have received your calibration request. Our team will review your presence and reach out shortly.
                </p>
                <button 
                  onClick={() => setStatus('idle')}
                  className="px-8 py-4 bg-white/5 hover:bg-[#BEF264] text-white hover:text-black rounded-xl text-[10px] font-black uppercase tracking-[0.2em] transition-all"
                >
                  Send Another
                </button>
              </motion.div>
            ) : (
              <form className="space-y-6 md:space-y-8 relative z-10" onSubmit={handleSubmit}>
                <div className="space-y-2">
                  <label className="text-[9px] font-black uppercase tracking-[0.4em] text-[#BEF264]/60"> Identity </label>
                  <input 
                    type="text" 
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                    placeholder="Full Name" 
                    className="w-full bg-transparent border-b border-white/10 py-3 md:py-4 text-white font-medium tracking-tight focus:border-[#BEF264] outline-none transition-colors placeholder:text-white/20" 
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[9px] font-black uppercase tracking-[0.4em] text-[#BEF264]/60"> Communication </label>
                  <input 
                    type="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder="Email Address" 
                    className="w-full bg-transparent border-b border-white/10 py-3 md:py-4 text-white font-medium tracking-tight focus:border-[#BEF264] outline-none transition-colors placeholder:text-white/20" 
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[9px] font-black uppercase tracking-[0.4em] text-[#BEF264]/60"> Current Presence </label>
                  <input 
                    type="text" 
                    name="presence"
                    value={formData.presence}
                    onChange={handleInputChange}
                    placeholder="Website or Social Handle" 
                    className="w-full bg-transparent border-b border-white/10 py-3 md:py-4 text-white font-medium tracking-tight focus:border-[#BEF264] outline-none transition-colors placeholder:text-white/20" 
                  />
                </div>
                
                {status === 'error' && (
                  <div className="text-red-500 text-[10px] font-bold uppercase tracking-widest">
                    Unable to submit. Please try again.
                  </div>
                )}

                <button 
                  type="submit" 
                  disabled={status === 'submitting'}
                  className="w-full bg-[#BEF264] text-black py-5 md:py-6 rounded-2xl font-black uppercase tracking-[0.3em] text-[10px] md:text-[11px] flex items-center justify-center gap-4 hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span>{status === 'submitting' ? 'Calibrating...' : 'Submit Inquiry'}</span>
                  <div className="w-4 h-[1px] bg-black" />
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;